/**
 * Поиск платья по фото клиента (экран «Чаты»): `POST /api/chats/{id}/dress-search` отдаёт похожие
 * модели каталога и что из них лежит в салоне выбранного города. «Предложить клиенту» кладёт в поле
 * ответа фото отмеченных (не отметили — 5 лучших) и текст со ссылкой `my.uni-renter.ru/p/<хэш>` на
 * остальные похожие платья города — отправляет сотрудник сам.
 * Город помним в браузере: у диалога его может не быть. Спека `docs/specs/2026-10-02-dress-search-design.md`.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";

import { type DressCard, dressPick, dressSearch, markDress } from "@/lib/clientChats";

const CITY_KEY = "prokat.dress.city";
export const MAX_PICK = 10;
/** Карточек на «страницу»: сервер отдаёт все похожие сразу, экран открывает их порциями. */
const PAGE = 24;
const savedCity = (): number | null => {
  const v = Number(localStorage.getItem(CITY_KEY));
  return Number.isInteger(v) && v > 0 ? v : null;
};
const sizes = (card: DressCard) => [...new Set(card.here.map((s) => s.size).filter(Boolean))].slice(0, 6).join(" · ");
const places = (card: DressCard) => {
  const free = card.here.filter((s) => s.available && s.place === "Зал").length;
  return free > 0 ? `в зале: ${free}${card.count > card.here.length ? "+" : ""}` : [...new Set(card.here.map((s) => s.place))].join(", ");
};

export async function photoFile(card: Pick<DressCard, "photo_url" | "model_id">): Promise<File> {
  const r = await fetch(card.photo_url, { credentials: "include" });
  if (!r.ok) throw new Error(`Фото модели ${card.model_id} не загрузилось`);
  const blob = await r.blob();
  const ext = blob.type === "image/webp" ? "webp" : blob.type === "image/png" ? "png" : "jpg";
  // Имя файла видит клиент — номер модели, не артикул.
  return new File([blob], `${card.model_id}.${ext}`, { type: blob.type || "image/jpeg" });
}

export function DressSearch({
  chatId,
  messageId,
  cityHint,
  onClose,
  onOffer,
}: {
  chatId: number;
  /** `null` — фото объявления Авито, по которому пишет клиент. */
  messageId: number | null;
  /** Город из контекста диалога (объявление Авито, аккаунт Instagram) — если совпадёт с салоном. */
  cityHint: string | null;
  onClose: () => void;
  onOffer: (text: string, files: File[]) => void;
}) {
  // Город: сохранённый в браузере; нет его — после первого ответа подсказка диалога; null — все города.
  const [city, setCity] = useState<number | null>(savedCity);
  const [picked, setPicked] = useState<number[]>([]);
  const [shown, setShown] = useState(PAGE);
  const [shownElse, setShownElse] = useState(PAGE / 2);
  const q = useQuery({
    queryKey: ["dress-search", chatId, messageId, city],
    queryFn: () => dressSearch(chatId, messageId, city),
    staleTime: Infinity,
    retry: false,
  });
  // Сохранённого города нет, а у диалога он известен — переключаемся на него один раз.
  useEffect(() => {
    if (city !== null || savedCity() !== null || !cityHint || !q.data) return;
    const hit = q.data.locations.find((l) => cityHint.toLowerCase().includes(l.name.toLowerCase()));
    if (hit) setCity(hit.id);
  }, [q.data, cityHint, city]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const offer = useMutation({
    mutationFn: async () => {
      const search_id = q.data!.search_id;
      const { text, cards } = await dressPick(chatId, search_id, picked);
      const files = await Promise.all(cards.map(photoFile));
      files.forEach((f, i) => markDress(f, { origin: "search", model_id: cards[i]!.model_id, search_id }));
      return { text, files };
    },
    onSuccess: ({ text, files }) => {
      onOffer(text, files);
      onClose();
    },
  });
  const toggle = (id: number) =>
    setPicked((had) => (had.includes(id) ? had.filter((x) => x !== id) : had.length < MAX_PICK ? [...had, id] : had));
  const choose = (value: string) => {
    const id = value ? Number(value) : null;
    if (id) localStorage.setItem(CITY_KEY, String(id));
    else localStorage.removeItem(CITY_KEY);
    setPicked([]);
    setShown(PAGE);
    setShownElse(PAGE / 2);
    setCity(id);
  };
  const cityName = q.data?.locations.find((l) => l.id === city)?.name;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 sm:p-4" onClick={onClose}>
      <div
        className="flex w-full max-w-[1080px] flex-col overflow-hidden bg-bg text-fg sm:rounded-[16px]"
        onClick={(e) => e.stopPropagation()}
        data-annot="поиск платья по фото"
      >
        <div className="flex flex-none flex-wrap items-center gap-3 border-b border-border px-4 py-2.5">
          <img
            src={messageId === null ? `/api/chats/${chatId}/item/photo` : `/api/chats/${chatId}/file/${messageId}?thumb=true`}
            alt={messageId === null ? "Фото объявления" : "Фото клиента"}
            className="size-12 flex-none rounded-[8px] object-cover"
          />
          <div className="min-w-0 text-[15px] font-medium">Похожие платья</div>
          <select
            value={city ?? ""}
            onChange={(e) => choose(e.target.value)}
            className="h-9 rounded-[8px] border border-border bg-bg px-2 text-[14px]"
            title="Салон, в котором ищем платья"
          >
            <option value="">Все города</option>
            {q.data?.locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <button type="button" onClick={onClose} title="Закрыть" className="ml-auto rounded p-1.5 text-fg-faint hover:bg-bg-hover hover:text-fg">
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-[13.5px]">
          {q.isLoading && <div className="py-10 text-center text-fg-faint">Ищем похожие платья…</div>}
          {q.isError && <div className="py-10 text-center text-[#e53935]">{(q.error as Error).message}</div>}
          {q.data && (
            <>
              {q.data.here.length === 0 && (
                <div className="py-6 text-center text-fg-faint">
                  {cityName ? `В салоне «${cityName}» похожих платьев нет` : "Похожих платьев в салонах нет"}
                </div>
              )}
              {q.data.here.length > 0 && (
                <div className="mb-2 text-fg-faint">
                  {cityName ? `Есть в салоне «${cityName}»` : "Есть в салонах"}: {q.data.here.length} — от самых похожих
                </div>
              )}
              <Grid cards={q.data.here.slice(0, shown)} picked={picked} onToggle={toggle} />
              <More shown={shown} total={q.data.here.length} onMore={() => setShown(shown + PAGE)} />
              {city !== null && q.data.elsewhere.length > 0 && (
                <>
                  <div className="mb-2 mt-5 font-medium text-fg-faint">Похожие, которых в этом городе нет: {q.data.elsewhere.length}</div>
                  <Grid cards={q.data.elsewhere.slice(0, shownElse)} picked={picked} />
                  <More shown={shownElse} total={q.data.elsewhere.length} onMore={() => setShownElse(shownElse + PAGE)} />
                </>
              )}
            </>
          )}
        </div>

        <div className="flex flex-none flex-wrap items-center gap-3 border-t border-border px-4 py-2.5 text-[13.5px]">
          <span className="text-fg-faint">
            {offer.isError ? (
              <span className="text-[#e53935]">{(offer.error as Error).message}</span>
            ) : (
              `Отмечено: ${picked.length} из ${MAX_PICK}`
            )}
          </span>
          <button
            type="button"
            disabled={!q.data?.here.length || offer.isPending}
            onClick={() => offer.mutate()}
            className="ml-auto h-9 rounded-[8px] bg-[var(--tg-primary)] px-4 font-medium text-white disabled:opacity-50"
            title="Фото и текст со ссылкой на остальные похожие платья города попадут в поле ответа — отправите сами"
            data-annot="предложить клиенту"
          >
            {offer.isPending ? "Готовим…" : `Предложить клиенту (${picked.length || "5 лучших"})`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** «Показать ещё»: следующая порция уже загруженных результатов. */
function More({ shown, total, onMore }: { shown: number; total: number; onMore: () => void }) {
  if (shown >= total) return null;
  return (
    <button
      type="button"
      onClick={onMore}
      className="mt-3 h-9 w-full rounded-[8px] border border-border font-medium text-[var(--tg-primary)] hover:bg-bg-hover"
    >
      Показать ещё {Math.min(PAGE, total - shown)} · показано {shown} из {total}
    </button>
  );
}

/** Сетка карточек; без `onToggle` — только посмотреть (платья нет в выбранном городе). */
function Grid({ cards, picked, onToggle }: { cards: DressCard[]; picked: number[]; onToggle?: (id: number) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {cards.map((c) => {
        const on = picked.includes(c.model_id);
        return (
          <button
            key={c.model_id}
            type="button"
            disabled={!onToggle}
            onClick={() => onToggle?.(c.model_id)}
            className={`relative flex flex-col overflow-hidden rounded-[10px] border text-left ${on ? "border-[var(--tg-primary)] ring-2 ring-[var(--tg-primary)]" : "border-border"} ${onToggle ? "hover:bg-bg-hover" : "cursor-default opacity-80"}`}
          >
            <img src={c.photo_url} alt={c.name} loading="lazy" className="aspect-[3/4] w-full bg-black/5 object-cover" />
            {onToggle && (
              <span
                className={`absolute right-2 top-2 grid size-6 place-items-center rounded-full border-2 border-white ${on ? "bg-[var(--tg-primary)] text-white" : "bg-black/25"}`}
              >
                {on && <Check size={14} />}
              </span>
            )}
            {c.score > 0 && (
              <span className="absolute left-2 top-2 rounded bg-black/55 px-1.5 py-0.5 text-[12px] text-white" title="Сходство с фото клиента">
                {Math.round(c.score * 100)}%
              </span>
            )}
            <span className="flex flex-col gap-0.5 px-2.5 py-2">
              <span className="font-medium">
                арт. {c.articles.slice(0, 2).join(", ")}
                {c.price ? ` · ${c.price} ₽` : ""}
              </span>
              <span className="truncate text-fg-faint">{[c.name, c.type].filter(Boolean).join(" · ") || " "}</span>
              {c.here.length > 0 ? (
                <>
                  <span className="truncate">{sizes(c) || "размер не указан"}</span>
                  <span className="truncate text-fg-faint">{places(c)}</span>
                </>
              ) : (
                <span className="text-fg-faint">{c.elsewhere.map((e) => `${e.city} (${e.count})`).join(", ")}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
